import { motion } from "framer-motion";
import { useRef } from "react";
import { CASE_STUDIES } from "../../lib/content";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useImages } from "../../lib/ImagesContext";

export default function CaseStudies() {
  const scrollRef = useRef(null);
  const { getImg } = useImages();

  const scrollByCards = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section className="relative py-24 md:py-36 overflow-hidden" data-testid="case-studies-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 mb-14">
          <div>
            <div className="tag-glass"><span className="dot" /> Selected work</div>
            <h2 className="font-display text-white text-4xl md:text-6xl leading-[1.02] tracking-tight mt-6">
              Scroll through <br />
              <span className="italic-accent text-grad-crimson">the campaigns</span>.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollByCards(-1)}
              aria-label="Previous campaigns"
              className="w-12 h-12 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-[#e63946] hover:bg-white/5 transition-colors flex items-center justify-center"
              data-testid="case-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollByCards(1)}
              aria-label="Next campaigns"
              className="w-12 h-12 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-[#e63946] hover:bg-white/5 transition-colors flex items-center justify-center"
              data-testid="case-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="pl-6 md:pl-10 pr-6 md:pr-10 overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        data-testid="case-rail"
      >
        <div className="flex gap-6 md:gap-8 w-max">
          {CASE_STUDIES.map((c, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              className="shrink-0 w-[85vw] md:w-[520px] snap-start"
              data-testid={`case-card-${i}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden border border-white/10 group">
                <img
                  src={getImg(`case-${i + 1}`, c.img)}
                  alt={c.title}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                  draggable={false}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/70 border border-white/20 rounded-full px-3 py-1 bg-black/30 backdrop-blur">
                    {c.tag}
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                  <div>
                    <h3 className="font-display text-white text-2xl md:text-3xl leading-tight tracking-tight max-w-[380px]">
                      {c.title}
                    </h3>
                    <p className="mt-3 font-mono text-[#e63946] text-sm">{c.metric}</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-white/70" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
